import React from "react";
import {
  Button,
  Collection,
  TreeItem,
  TreeItemContent,
  type TreeItemContentRenderProps,
} from "react-aria-components";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import { Icon } from "../../Icon";
import { Text } from "../../Text";

import styles from "./styles.module.css";
import type { TreeNodeItem } from "./types";

interface TreeNodeRowProps {
  node: TreeNodeItem;
}

/**
 * One row of the WDS Tree: RAC GA TreeItem + TreeItemContent. Chevron and
 * indentation are driven by the TreeItemContent RENDER PROPS
 * (isExpanded / level / hasChildItems) — never by walking the data again:
 *
 * - branches render a RAC `slot="chevron"` button (the GA expand/collapse
 *   affordance; RAC wires its press to toggle + `aria-expanded`);
 * - leaves reserve the same inline space so labels align per level;
 * - `level` feeds the `--tree-item-level` custom property that styles.module.css
 *   turns into indentation (WDS spacing tokens).
 *
 * `textValue` is the plain label, enabling RAC typeahead. The label itself
 * renders through <Text> as a plain string (React-escaped) — the tree-family
 * security posture.
 */
export const TreeNodeRow = (props: TreeNodeRowProps) => {
  const { node } = props;

  return (
    <TreeItem className={styles.item} id={node.id} textValue={node.label}>
      <TreeItemContent>
        {({ hasChildItems, isExpanded, level }: TreeItemContentRenderProps) => (
          <div
            className={styles.itemContent}
            style={{ "--tree-item-level": level } as React.CSSProperties}
          >
            {hasChildItems ? (
              <Button className={styles.chevron} slot="chevron">
                <Icon
                  name={isExpanded ? "chevron-down" : "chevron-right"}
                  size="small"
                />
              </Button>
            ) : (
              <span aria-hidden className={styles.chevronSpacer} />
            )}
            <Text className={styles.label} lineClamp={1} size="body">
              {node.label}
            </Text>
          </div>
        )}
      </TreeItemContent>
      <Collection items={node.children ?? []}>
        {(child: TreeNodeItem) => <TreeNodeRow node={child} />}
      </Collection>
    </TreeItem>
  );
};
